import React, { ComponentType, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { maskPhone } from 'modules/utils/MaskUtils';
import { MuiContainer, MuiTypography } from 'theme/material-ui';
import Spacer from 'components/UI/Layout/Spacer';
import Footer from 'components/common/Footer';
import dayjs from 'dayjs';

import { RootState } from 'store/types';
import * as covidScreeningSelectors from 'store/CovidScreening/selectors';
import { Insurance, Grantor } from 'store/profile/types';
import { profileConsumerMilleniumInsuranceSelector } from 'store/profile/selectors';
import { CovidTitles } from 'lib/constants/covidScreening';
import { Color } from 'modules/styles/colors';
import { SpinnerOverlay } from 'components/UI/Spinner/SpinnerModal';
import { AnalyticsEvent } from 'services/AnalyticsService';
import useNavigationAnalytics from 'hooks/useNavigationAnalytics';
import * as covidActions from 'store/CovidScreening/actions';
import { Patient, Questionnaire, CovidScreeningState } from 'store/CovidScreening/types';
import { patientSelect } from 'modules/constants/covidScreening';
import { FontSize, FontWeight } from 'modules/styles/variables';
import useStyles from '../../CovidScreening/Components/CovidTestButton/useStyles';
import { CovidTestButton } from '../../CovidScreening/Components/CovidTestButton';

export interface Props extends RouteComponentProps {
  patientsData: Patient;
  questionnaire: Questionnaire;
  covidScreeningData: CovidScreeningState;
  sendCovidScreeningFinalData: typeof covidActions.sendCovidScreeningData;
  setCovidScreeningOrderStatus: typeof covidActions.covidOrderStatus;
  activeInsurance: Insurance[];
  grantors: Grantor[];
}

export function CovidScreenGuestSummary({
  history,
  questionnaire,
  patientsData,
  covidScreeningData
}: Props) {
  const covidTestSummaryPrev = useNavigationAnalytics(AnalyticsEvent.CovidPrev);
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = () => {
    covidTestSummaryPrev({
      title: CovidTitles.PATIENT_SUMMARY
    });
    history.goBack();
  };

  return (
    <>
      <MuiContainer maxWidth="lg">
        <SpinnerOverlay
          isLoading={isLoading}
          position="absolute"
          backgroundColor={Color.transparent}
          testID="Covid-Order-Submit"
        />
        <Spacer size="medium" />
        <MuiTypography fontSize={FontSize.medium} fontWeight={FontWeight.bold}>
          Patient Information Summary
        </MuiTypography>
        <Spacer size="xsmall" />
        <MuiTypography fontWeight={FontWeight.semibold} display="inline">
          Patient Name:{' '}
        </MuiTypography>{' '}
        <MuiTypography display="inline">
          {patientsData?.names[0]?.givenName} {patientsData?.names[0]?.middleName}{' '}
          {patientsData?.names[0]?.familyName}
        </MuiTypography>
        <MuiTypography>
          <MuiTypography fontWeight={FontWeight.semibold} display="inline">
            Patient Phone:{' '}
          </MuiTypography>{' '}
          <MuiTypography display="inline">
            {maskPhone(
              patientsData?.phones[0]?.areaCode.concat(patientsData?.phones[0]?.phoneNumber)
            )}
          </MuiTypography>
        </MuiTypography>
        <MuiTypography>
          <MuiTypography fontWeight={FontWeight.semibold} display="inline">
            Patient Alt Phone (if provided):
          </MuiTypography>{' '}
          <MuiTypography display="inline">{patientsData?.alternateNumber}</MuiTypography>
        </MuiTypography>
        <MuiTypography>
          {' '}
          <MuiTypography fontWeight={FontWeight.semibold} display="inline">
            {' '}
            Patient Email:{' '}
          </MuiTypography>
          <MuiTypography display="inline"> {patientsData?.emails[0]?.address} </MuiTypography>
        </MuiTypography>
        <MuiTypography>
          <MuiTypography fontWeight={FontWeight.semibold} display="inline">
            {' '}
            Patient Birthdate:{' '}
          </MuiTypography>
          <MuiTypography display="inline"> {patientsData?.dateOfBirth} </MuiTypography>
        </MuiTypography>
        <MuiTypography>
          <MuiTypography fontWeight={FontWeight.semibold} display="inline">
            {' '}
            Patient Sex:{' '}
          </MuiTypography>
          <MuiTypography display="inline"> {patientsData?.genderType} </MuiTypography>
        </MuiTypography>
        <MuiTypography>
          <MuiTypography fontWeight={FontWeight.semibold} display="inline">
            Patient Address:{' '}
          </MuiTypography>
          <MuiTypography>
            {`${patientsData?.addresses[0]?.line1 ?? ''} ${patientsData.addresses[0]?.line2 ?? ''}`}
          </MuiTypography>
          <MuiTypography>
            {`${patientsData?.addresses[0]?.city ?? ''} ${patientsData?.addresses[0]?.state ??
              ''} ${patientsData?.addresses[0]?.zip ?? ''}`}
          </MuiTypography>
        </MuiTypography>
        <Spacer size="small" />
        <MuiTypography>
          <MuiTypography fontWeight={FontWeight.semibold} display="inline">
            {' '}
            Do you have insurance?
          </MuiTypography>{' '}
          <MuiTypography display="inline"> {questionnaire?.has_insurance} </MuiTypography>
        </MuiTypography>
        {questionnaire?.has_insurance === 'Yes' ? (
          <MuiTypography>
            <MuiTypography fontWeight={FontWeight.semibold} display="inline">
              {' '}
              Have you been seen at Intermountain?
            </MuiTypography>{' '}
            <MuiTypography display="inline"> {questionnaire?.have_been_patient} </MuiTypography>
          </MuiTypography>
        ) : null}
        {questionnaire?.have_been_patient === 'Yes' ? (
          <MuiTypography>
            <MuiTypography fontWeight={FontWeight.semibold} display="inline">
              {' '}
              Need to change insurance?
            </MuiTypography>{' '}
            <MuiTypography display="inline">
              {' '}
              {questionnaire?.have_changed_insurance}{' '}
            </MuiTypography>
          </MuiTypography>
        ) : null}
        {questionnaire?.MEMBER_NUMBER ? (
          <>
            <Spacer size="small" />
            {questionnaire?.has_insurance === 'Yes' ? (
              <MuiTypography fontWeight={FontWeight.bold}>Insurance Information</MuiTypography>
            ) : null}
            {questionnaire?.Health_Plan_Name && questionnaire?.has_insurance === 'Yes' ? (
              <MuiTypography>
                <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                  {' '}
                  Plan Network:{' '}
                </MuiTypography>
                <MuiTypography display="inline"> {questionnaire?.Health_Plan_Name} </MuiTypography>
              </MuiTypography>
            ) : null}
            {questionnaire?.MEMBER_NUMBER && questionnaire?.has_insurance === 'Yes' ? (
              <MuiTypography>
                <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                  {' '}
                  Member Number/ID:{' '}
                </MuiTypography>
                <MuiTypography display="inline"> {questionnaire?.MEMBER_NUMBER} </MuiTypography>
              </MuiTypography>
            ) : null}
            {questionnaire?.GROUP_NUMBER && questionnaire?.has_insurance === 'Yes' ? (
              <MuiTypography>
                <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                  {' '}
                  Group Number/ID:{' '}
                </MuiTypography>
                <MuiTypography display="inline"> {questionnaire?.GROUP_NUMBER} </MuiTypography>
              </MuiTypography>
            ) : null}
            {questionnaire?.has_insurance === 'No' ? <Spacer size="small" /> : null}
            {questionnaire?.Q_Patient_relationship_to_subscriber &&
              questionnaire?.Q_Patient_relationship_to_subscriber !== 'Self' && (
                <>
                  <Spacer size="small" />
                  <MuiTypography fontWeight={FontWeight.bold}>Subscriber Information</MuiTypography>
                  <Spacer size="xsmall" />
                  <MuiTypography>
                    <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                      Relationship to subscriber:{' '}
                    </MuiTypography>{' '}
                    <MuiTypography display="inline">
                      {' '}
                      {questionnaire?.Q_Patient_relationship_to_subscriber}
                    </MuiTypography>{' '}
                  </MuiTypography>
                  <MuiTypography>
                    <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                      {' '}
                      Subscriber Name:{' '}
                    </MuiTypography>
                    <MuiTypography display="inline">
                      {covidScreeningData?.subscriber.subscriberFirstName}{' '}
                      {covidScreeningData?.subscriber.subscriberMiddleName}{' '}
                      {covidScreeningData?.subscriber.subscriberLastName}{' '}
                    </MuiTypography>
                  </MuiTypography>
                  <MuiTypography>
                    <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                      {' '}
                      Subscriber Phone:{' '}
                    </MuiTypography>
                    <MuiTypography display="inline">
                      {covidScreeningData?.subscriber.subscriberCellNumber}{' '}
                    </MuiTypography>
                  </MuiTypography>
                  <MuiTypography>
                    <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                      Subscriber Birthday:
                    </MuiTypography>{' '}
                    <MuiTypography display="inline">
                      {' '}
                      {dayjs(covidScreeningData?.subscriber.subscriberDateOfBirth).format(
                        'MM/DD/YYYY'
                      )}{' '}
                    </MuiTypography>{' '}
                  </MuiTypography>
                  <MuiTypography>
                    <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                      {' '}
                      Subscriber Email:
                    </MuiTypography>{' '}
                    <MuiTypography display="inline">
                      {covidScreeningData?.subscriber.subscriberEmail}{' '}
                    </MuiTypography>{' '}
                  </MuiTypography>
                  <MuiTypography>
                    <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                      Subscriber Sex:
                    </MuiTypography>{' '}
                    <MuiTypography display="inline">
                      {covidScreeningData?.subscriber.subscriberSex}
                    </MuiTypography>{' '}
                  </MuiTypography>
                  <MuiTypography fontWeight={FontWeight.semibold} display="inline">
                    Subscriber Address:{' '}
                  </MuiTypography>
                  <MuiTypography>
                    {`${covidScreeningData?.subscriber.subscriberAddressLine1} ${covidScreeningData?.subscriber.subscriberAddressLine2}`}
                  </MuiTypography>
                  <MuiTypography>
                    {`${covidScreeningData?.subscriber.subscriberCity} ${covidScreeningData?.subscriber.subscriberState} ${covidScreeningData?.subscriber.subscriberZip}`}
                  </MuiTypography>
                </>
              )}
          </>
        ) : null}
        <Spacer size="small" />
        <MuiTypography>
          If you would like to correct or change the information provided, please select the
          Previous button. If all information looks correct, proceed to see a list of available
          testing sites.
        </MuiTypography>
        <Spacer size="small" />
        <MuiContainer className={classes.steppersFooter}>
          <CovidTestButton fullWidth disabled variant="contained" data-testid="summary-next-button">
            Submit
          </CovidTestButton>
          <CovidTestButton
            fullWidth
            color="default"
            variant="contained"
            data-testid="summary-previous-button"
            onClick={handleCancel}
          >
            Previous
          </CovidTestButton>
        </MuiContainer>
        <Spacer size="small" />
        <Footer
          headerText={patientSelect.GET_PATIENTS.HELPLINE.CovidInfo}
          link={patientSelect.GET_PATIENTS.HELPLINE.CovidPatientSelectHelpline}
          linkName={patientSelect.GET_PATIENTS.HELPLINE.CovidPatientSelectHelpline}
          helplineHours={patientSelect.GET_PATIENTS.HELPLINE.CovidHelplineHours}
          title={CovidTitles.PATIENT_SUMMARY}
        />
        <Spacer size="small" />
      </MuiContainer>
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  questionnaire: covidScreeningSelectors.CovidScreeningQuestionnaireSelector(state),
  patientsData: covidScreeningSelectors.CovidScreeningPatientInfoSelector(state),
  covidScreeningData: covidScreeningSelectors.CovidScreeningSelector(state),
  subscriberInfo: covidScreeningSelectors.CovidScreeningSubscriberSelector(state),
  activeInsurance: profileConsumerMilleniumInsuranceSelector(state)
});

const mapDispatchToProps = {
  sendCovidScreeningFinalData: covidActions.sendCovidScreeningData,
  setCovidScreeningOrderStatus: covidActions.covidOrderStatus
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CovidScreenGuestSummary as ComponentType);
